import React, { useEffect, useState, useRef } from 'react';
import { BrowserRouter as Router, Routes, Route, Link, Navigate } from 'react-router-dom';
import { Toaster } from 'react-hot-toast';
import './App.css';
import { useAuthStore } from './store/authStore';
import ProtectedRoute from './components/ProtectedRoute';
import Sidebar from './components/Sidebar';
import { getAccessibleModules } from './utils/moduleAccess';
// We derive effective permissions purely from the role on the frontend
// using static defaults, instead of relying on the dynamic permissions API.
// This keeps the UI simple and predictable.
import { getDefaultPermissionsForRole } from './utils/moduleAccess';

// Import pages
import Login from './pages/Login';
import Register from './pages/Register';
import Dashboard from './pages/Dashboard';
import ClientDashboard from './pages/ClientDashboard';
import SuperadminDashboard from './pages/SuperadminDashboard';
import OperadorDashboard from './pages/OperadorDashboard';
import DistribuidorDashboard from './pages/DistribuidorDashboard';
import Companies from './pages/Companies';
import Users from './pages/Users';
import Devices from './pages/Devices';
import DevicesView from './pages/DevicesView';
import Depots from './pages/Depots';
import BoxAssignments from './pages/BoxAssignments';
import ContainerAssignment from './pages/ContainerAssignment';
import Notifications from './pages/Notifications';
import Reports from './pages/Reports';
import AuditLogs from './pages/AuditLogs';
import PasswordReset from './pages/PasswordReset';
import Settings from './pages/Settings';
import Permissions from './pages/Permissions';

// Dashboard wrapper that renders appropriate dashboard based on user type
function DashboardWrapper() {
  const user = useAuthStore((state) => state.user);
  
  if (user?.user_type === 'gods_eye') {
    return <SuperadminDashboard />;
  }
  
  if (user?.user_type === 'manager') {
    return <SuperadminDashboard />;
  }
  
  if (user?.user_type === 'cliente') {
    return <ClientDashboard />;
  }

  if (user?.user_type === 'operador') {
    return <OperadorDashboard />;
  }

  if (user?.user_type === 'distribuidor') {
    return <DistribuidorDashboard />;
  }
  
  return <Dashboard />;
}

function App() {
  const [isInitialized, setIsInitialized] = useState(false);
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const token = useAuthStore((state) => state.token);
  const user = useAuthStore((state) => state.user);
  const logout = useAuthStore((state) => state.logout);
  const setToken = useAuthStore((state) => state.setToken);
  const setUser = useAuthStore((state) => state.setUser);
  const setPermissions = useAuthStore((state) => state.setPermissions);

  useEffect(() => {
    // Initialize app - validate token exists on mount and load permissions
    const init = async () => {
      const storedToken = localStorage.getItem('token');
      const storedUser = localStorage.getItem('user');

      // If no token or user stored, clear everything
      if (!storedToken || !storedUser) {
        localStorage.removeItem('token');
        localStorage.removeItem('user');
        sessionStorage.clear();
        setToken(null);
        setUser(null);
        setPermissions(null);
        setIsInitialized(true);
        return;
      }

      try {
        const parsedUser = JSON.parse(storedUser);
        setUser(parsedUser);

        // Derive permissions purely from user_type using static defaults.
        if (parsedUser?.user_type) {
          const permissionsMap = getDefaultPermissionsForRole(parsedUser.user_type);
          setPermissions(permissionsMap);
        } else {
          setPermissions(null);
        }
      } catch (err) {
        console.error('Failed to initialize auth state', err);
        setPermissions(null);
      } finally {
        setIsInitialized(true);
      }
    };

    init();
  }, [setToken, setUser, setPermissions]);

  // Show nothing while app is initializing
  if (!isInitialized) {
    return <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: '100vh', backgroundColor: '#f9fafb' }} />;
  }

  if (!token) {
    return (
      <Router>
        <Routes>
          <Route path="/login" element={<Login />} />
          <Route path="/register" element={<Register />} />
          <Route path="/reset-password" element={<PasswordReset />} />
          <Route path="*" element={<Navigate to="/login" replace />} />
        </Routes>
        <Toaster position="top-right" />
      </Router>
    );
  }

  return (
    <Router>
      <div className="app-layout" key={user?.id || user?.username || 'anonymous'}>
        {/* Sidebar */}
        <Sidebar isOpen={sidebarOpen} onClose={() => setSidebarOpen(false)} />

        {/* Main content area */}
        <div className="app-container">
          {/* Top bar with hamburger menu */}
          <header className="app-header">
            <button 
              className="hamburger-btn"
              onClick={() => setSidebarOpen(!sidebarOpen)}
              aria-label="Toggle sidebar"
            >
              <span></span>
              <span></span>
              <span></span>
            </button>
          </header>

          {/* Page content */}
          <main className="main-content">
            <Routes>
              <Route path="/" element={<DashboardWrapper />} />
              <Route path="/devices-view" element={<DevicesView />} />
              <Route path="/container-assignment" element={<ProtectedRoute requiredModule="container_assignment"><ContainerAssignment /></ProtectedRoute>} />
              <Route path="/companies" element={<ProtectedRoute requiredModule="companies"><Companies /></ProtectedRoute>} />
              <Route path="/users" element={<ProtectedRoute requiredModule="users"><Users /></ProtectedRoute>} />
              <Route path="/devices" element={<ProtectedRoute requiredModule="devices"><Devices /></ProtectedRoute>} />
              <Route path="/depots" element={<ProtectedRoute requiredModule="depots"><Depots /></ProtectedRoute>} />
              <Route path="/box-assignments" element={<ProtectedRoute requiredModule="device_assignments"><BoxAssignments /></ProtectedRoute>} />
              <Route path="/notifications" element={<ProtectedRoute requiredModule="notifications"><Notifications /></ProtectedRoute>} />
              <Route path="/reports" element={<ProtectedRoute requiredModule="reports"><Reports /></ProtectedRoute>} />
              <Route path="/audit-logs" element={<ProtectedRoute requiredModule="audit_logs"><AuditLogs /></ProtectedRoute>} />
              <Route path="/permissions" element={<Permissions />} />
              <Route path="/settings" element={<Settings />} />
              <Route path="/login" element={<Navigate to="/" replace />} />
              <Route path="/register" element={<Navigate to="/" replace />} />
            </Routes>
          </main>
        </div>

        <Toaster position="top-right" />
      </div>
    </Router>
  );
}

export default App;
